import { Divider, MenuItem, Stack, Typography } from '@mui/material';
import { styled } from '@mui/material/styles';
import { useNavigate } from 'react-router-dom';
import type { User } from '../../../entities/user';
import { UserAvatar } from '../../../entities/user';
import { useI18n } from '../../../shared/i18n';
import { TranslatedText } from '../../../shared/ui/TranslatedText';
import { ProfileStatusBadge } from '../../../shared/ui/ProfileStatusBadge';

const ProfileSummary = styled(Stack)(({ theme }) => ({
  minWidth: 280,
  maxWidth: 320,
  padding: theme.spacing(1.5),
  gap: theme.spacing(1.25),
}));

const ProfileIdentityRow = styled(Stack)(({ theme }) => ({
  flexDirection: 'row',
  alignItems: 'center',
  gap: theme.spacing(1.25),
}));

const ProfileCopy = styled(Stack)(({ theme }) => ({
  minWidth: 0,
  gap: theme.spacing(0.25),
}));

const ProfileDetails = styled(Stack)(({ theme }) => ({
  gap: theme.spacing(0.5),
  paddingTop: theme.spacing(1),
}));

const StatusRow = styled(Stack)(({ theme }) => ({
  flexDirection: 'row',
  alignItems: 'center',
  gap: theme.spacing(0.5),
}));

const BreakableText = styled(Typography)({
  wordBreak: 'break-word',
});

interface ProfileCardPopoverProps {
  user: User | null;
  onAuthorize: () => void;
  onLogout: () => void;
}

export function ProfileCardPopover({ user, onAuthorize, onLogout }: ProfileCardPopoverProps) {
  const { t } = useI18n();
  const navigate = useNavigate();

  const guestName = t('app.profile.guestName');
  const firstName =
    user && typeof (user as { firstName?: string | null }).firstName === 'string'
      ? (user as { firstName?: string | null }).firstName
      : null;
  const lastName =
    user && typeof (user as { lastName?: string | null }).lastName === 'string' ? (user as { lastName?: string | null }).lastName : null;
  const firstNameValue = firstName?.trim() ? firstName.trim() : '';
  const lastNameValue = lastName?.trim() ? lastName.trim() : '';
  const phoneValue = user?.phone?.trim() ? user.phone : '';
  const nameParts = [firstNameValue, lastNameValue].filter(Boolean);
  const displayName = nameParts.length ? nameParts.join(' ') : t('app.profile.unnamed');

  return (
    <>
      <ProfileSummary>
        <ProfileIdentityRow>
          <UserAvatar user={user} size={42} />
          <ProfileCopy>
            <TranslatedText messageKey="app.profile.title" variant="subtitle1" />
            <BreakableText variant="body2" color="text.secondary">
              {user ? displayName : guestName}
            </BreakableText>
          </ProfileCopy>
        </ProfileIdentityRow>

        {user && (
          <ProfileDetails>
            {phoneValue ? (
              <BreakableText variant="body2">
                {phoneValue}
              </BreakableText>
            ) : null}

            <StatusRow>
              <ProfileStatusBadge
                kycLevel={user.kycLevel}
                firstName={firstName}
              />
            </StatusRow>
          </ProfileDetails>
        )}
      </ProfileSummary>
      <Divider />

      {!user ? (
        <MenuItem onClick={onAuthorize}>{t('app.profile.login')}</MenuItem>
      ) : (
        <>
          <MenuItem onClick={() => navigate('/profile')}>{t('app.profile.edit')}</MenuItem>
          <MenuItem onClick={onLogout}>{t('app.profile.logout')}</MenuItem>
        </>
      )}
    </>
  );
}
